import { getImageProps } from 'next/image';
import { UmbracoTypes } from '~/lib/data-contract';
import { umbracoLoader } from '~/shared/components/Image/loaders/umbracoLoader';
import { Spacer } from '~/shared/components/Spacer';
import { AlignContent } from '~/templates/blocks/shared/AlignContent';
import { Headline } from '~/templates/blocks/shared/Headline';
import { queries } from '~/theme/breakpoints';
import { useModule } from '../../hooks/useModule';
import { useModuleTheme } from '../../hooks/useModuleTheme';
import { ModuleContainer } from '../../shared/ModuleContainer';
import getFocalPoint from '../../utils/getFocalPoint';
import { StyledM75Tiny, StyledM75TinyContent, StyledMediaArea } from './styled';
import { Text } from '~/shared/components/Text';

import { M75TinyMediaTextProps } from './types';

export type { M75TinyMediaTextProps } from './types';

export const M75TinyMediaText = ({
    image,
    headline,
    description,
    backgroundColor: initialBackgroundColor,
    backgroundColorHEX: initialBackgroundColorHex,
    hasGutter = true,
    isVisible = false,
    mediaHorizontalAlignment,
    showOnPath,
    ...rest
}: M75TinyMediaTextProps) => {
    const { index } = useModule();
    const { textColor, backgroundColor } = useModuleTheme({
        backgroundColor: initialBackgroundColorHex || initialBackgroundColor,
    });
    const {
        props: { srcSet: desktop, ...desktopProps },
    } = getImageProps({
        loader: umbracoLoader,
        alt: image?.alt ?? headline ?? '',
        src: image?.src ?? '',
        fill: true,
        loading: index < 2 ? 'eager' : 'lazy',
        style: {
            objectFit: 'cover',
            objectPosition: getFocalPoint(image),
        },
    });

    return (
        <ModuleContainer hasGutter={hasGutter} {...rest}>
            <StyledM75Tiny>
                <StyledM75TinyContent
                    backgroundColor={backgroundColor}
                    mediaAlignment={mediaHorizontalAlignment}
                    isVisible={isVisible}
                >
                    <AlignContent textColor={textColor}>
                        {headline ? <Headline variant="display2">{headline}</Headline> : null}
                        {headline && description ? <Spacer size="4" /> : null}
                        {description ? <Text>{description}</Text> : null}
                    </AlignContent>
                    {image?.src ? (
                        <StyledMediaArea>
                            <picture>
                                <source media={`${queries.md}`} srcSet={desktop} />
                                <source srcSet={desktop} />
                                <img {...desktopProps} alt={image?.alt} />
                            </picture>
                        </StyledMediaArea>
                    ) : null}
                </StyledM75TinyContent>
            </StyledM75Tiny>
        </ModuleContainer>
    );
};

function ShouldShowM75TinyModule(
    currentFilters: string[][][],
    currentPage: string,
    moduleFilters: string[][][],
    modulePath: string,
    mode: 'exact' | 'contains' = 'exact',
) {
    const pathMatches = modulePath ? currentPage === modulePath : true;

    // If module doesn't specify any filters but matches the current path, the module should be shown
    if (!moduleFilters || moduleFilters.length === 0) return pathMatches;

    // Convert filters into Map<key, values[]> so we can compare them easily
    const toMap = (filters?: string[][][]): Map<string, string[]> => {
        const map = new Map<string, string[]>();
        if (!filters) return map;
        for (const entry of filters) {
            const key = Array.isArray(entry[0]) && entry[0].length ? String(entry[0][0]) : '';
            const vals = Array.isArray(entry[1]) ? entry[1].map(String) : [];
            if (!key) continue;
            const existing = map.get(key) ?? [];
            map.set(key, Array.from(new Set([...existing, ...vals])));
        }
        return map;
    };

    const chosenMap = toMap(currentFilters ?? undefined);
    const moduleMap = toMap(moduleFilters ?? undefined);

    const intersectionExists = (a: string[], b: string[]) => a.some((v) => b.includes(v));

    // 'contains' means: every module key must be present in chosen filters and share at least one value
    if (mode === 'contains') {
        const moduleFiltersMatchesChosenFilters = Array.from(moduleMap.entries()).every(
            ([key, moduleVals]) => {
                const chosenVals = chosenMap.get(key) ?? [];
                return chosenVals.length > 0 && intersectionExists(moduleVals, chosenVals);
            },
        );
        return moduleFiltersMatchesChosenFilters && pathMatches;
    }

    // 'exact' means all filters have the same keys and values
    const moduleKeys = Array.from(moduleMap.keys()).sort();
    const chosenKeys = Array.from(chosenMap.keys()).sort();
    if (moduleKeys.length !== chosenKeys.length) return false && pathMatches;
    for (let i = 0; i < moduleKeys.length; i++) {
        if (moduleKeys[i] !== chosenKeys[i]) return false && pathMatches;
        const modVals = (moduleMap.get(moduleKeys[i]) ?? []).slice().sort();
        const chosenVals = (chosenMap.get(chosenKeys[i]) ?? []).slice().sort();
        if (modVals.length !== chosenVals.length) return false && pathMatches;
        for (let j = 0; j < modVals.length; j++)
            if (modVals[j] !== chosenVals[j]) return false && pathMatches;
    }

    return pathMatches;
}

export function PageHeroElement(
    pageHeroElements: UmbracoTypes.IModules[] | undefined,
    asPath: string,
) {
    const currentPage = decodeURI(asPath)?.split('?')[0];

    const currentFilters =
        decodeURI(asPath)
            ?.split('?')[1]
            ?.split('&')
            ?.filter((item) => item.indexOf('CategoryPath')) // We don't care about this menu
            ?.map((item) => item.split('=').map((item) => item.split('%3B'))) ?? null;

    let filteredHeroElements = (pageHeroElements as UmbracoTypes.IM75TinyMediaAndTextModule[])
        ?.map((item) => {
            const moduleFilters = !!item.showOnPath
                ? item.showOnPath
                      ?.split('?')[1]
                      ?.split('&')
                      ?.map((item) => item.split('=').map((item) => item.split('%3B')))
                : [];
            const modulePage = !!item.showOnPath ? item.showOnPath?.split('?')[0] : '';

            item.isVisible = ShouldShowM75TinyModule(
                currentFilters,
                currentPage,
                moduleFilters,
                modulePage,
                item.exactMatch ? 'exact' : 'contains',
            );

            return item;
        })
        .filter((item) => item);

    if (!Boolean(filteredHeroElements.filter((item) => item.isVisible).length))
        filteredHeroElements = filteredHeroElements.filter(
            (item) => (item.isVisible = !!!item.showOnPath),
        );

    return Boolean(filteredHeroElements.filter((item) => item.isVisible).length)
        ? [filteredHeroElements.filter((item) => item.isVisible)[0]]
        : null;
}
